import React, { useEffect, useState } from 'react'
import { Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { DonationsAdmin } from './DonationsAdmin'
import { EventsAdmin } from './EventsAdmin'
import { EventForm } from './EventForm'
import { CompaniesAdmin } from './CompaniesAdmin'
import { MessagesAdmin } from './MessagesAdmin'
import { UsersAdmin } from './UsersAdmin'
import { SettingsAdmin } from './SettingsAdmin'
import { MediaAdmin } from './MediaAdmin'
import { MediaUploadForm } from './MediaUploadForm'
import { PlantingsAdmin } from './PlantingsAdmin'
import { BlogAdmin, BlogPostForm } from './BlogAdmin'
import { PlantingForm } from './PlantingForm'
import { RegionsAdmin } from './RegionsAdmin'
import { LettersCertificatesAdmin } from './LettersCertificatesAdmin'
import {
  MdPayment,
  MdEvent,
  MdBusiness,
  MdMessage,
  MdPeople,
  MdMenu,
  MdSettings,
  MdPhotoLibrary,
  MdPark,
  MdArticle,
  MdMap,
  MdMail
} from 'react-icons/md'
import { IconType } from 'react-icons'
import adminApi from './api'
import './admin-reset.css'
import './admin-styles.css'
import './AdminDashboard.css'

export const AdminDashboard: React.FC = () => {
  const [user, setUser] = useState<any>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const userData = localStorage.getItem('admin_user')
    if (userData) {
      setUser(JSON.parse(userData))
    } else {
      navigate('/admin/login')
    }
  }, [navigate])

  // Fetch unread messages count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const { data } = await adminApi.get('/api/admin/messages/unread-count')
        setUnreadCount(data.count)
      } catch (err) {
        console.error('Error fetching unread count:', err)
      }
    }

    if (user) {
      fetchUnreadCount()

      // Listen for message read events
      const handleMessageRead = () => {
        fetchUnreadCount()
      }
      window.addEventListener('messageRead', handleMessageRead)

      return () => {
        window.removeEventListener('messageRead', handleMessageRead)
      }
    }
  }, [user])

  const handleLogout = () => {
    setShowLogoutModal(true)
  }

  const confirmLogout = () => {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    navigate('/admin/login')
  }

  const toggleSidebar = () => {
    setCollapsed(!collapsed)
  }

  if (!user) return null

  const getBreadcrumb = () => {
    const path = location.pathname

    if (path === '/admin/donations') return 'Платежи'
    if (path === '/admin/events') return 'События'
    if (path === '/admin/events/new') return 'Создать событие'
    if (path.startsWith('/admin/events/') && path.endsWith('/edit')) return 'Редактировать событие'
    if (path === '/admin/companies') return 'Партнёры'
    if (path === '/admin/letters-certificates') return 'Письма и сертификаты'
    if (path === '/admin/messages') return 'Сообщения'
    if (path === '/admin/media') return 'Медиа'
    if (path === '/admin/media/new') return 'Загрузить медиа'
    if (path === '/admin/plantings') return 'Посадки'
    if (path === '/admin/plantings/new') return 'Новая посадка'
    if (path.startsWith('/admin/plantings/') && path.endsWith('/edit')) return 'Редактировать посадку'
    if (path === '/admin/blog') return 'Блог'
    if (path === '/admin/blog/new') return 'Новая статья'
    if (path.startsWith('/admin/blog/') && path.endsWith('/edit')) return 'Редактировать статью'
    if (path === '/admin/regions') return 'Регионы'
    if (path === '/admin/users') return 'Пользователи'
    if (path === '/admin/settings') return 'Настройки'

    return 'Главная'
  }

  const menuItems: Array<{ path: string; label: string; icon: IconType }> = [
    { path: '/admin/donations', label: 'Платежи', icon: MdPayment },
    { path: '/admin/events', label: 'События', icon: MdEvent },
    { path: '/admin/plantings', label: 'Посадки', icon: MdPark },
    { path: '/admin/regions', label: 'Регионы', icon: MdMap },
    { path: '/admin/companies', label: 'Партнёры', icon: MdBusiness },
    { path: '/admin/letters-certificates', label: 'Письма и сертификаты', icon: MdMail },
    { path: '/admin/media', label: 'Медиа', icon: MdPhotoLibrary },
    { path: '/admin/messages', label: 'Сообщения', icon: MdMessage },
    { path: '/admin/blog', label: 'Блог', icon: MdArticle },
  ]

  if (user.role === 'admin') {
    menuItems.push({ path: '/admin/users', label: 'Пользователи', icon: MdPeople })
    menuItems.push({ path: '/admin/settings', label: 'Настройки', icon: MdSettings })
  }

  // Readonly-режим (например iframe модалки превью): без sidebar/header
  const isReadonly = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('readonly') === '1'
  if (isReadonly) {
    return (
      <Routes>
        <Route path="/letters-certificates" element={<LettersCertificatesAdmin />} />
      </Routes>
    )
  }

  return (
    <div className="admin-layout">
      {/* Top Header */}
      <header className="admin-top-header">
        <div className="admin-header-left">
          <button onClick={toggleSidebar} className="sidebar-toggle-btn">
            {React.createElement(MdMenu as any, { size: 24 })}
          </button>
          <div className="admin-logo">
            <span>Жашыл дем</span>
          </div>
        </div>
        <div className="admin-user-section">
          <div className="admin-user-info">
            <p className="admin-user-name">{user.name}</p>
            <p className="admin-user-role">{user.role === 'admin' ? 'Админ' : 'Менеджер'}</p>
          </div>
          <button onClick={handleLogout} className="admin-logout-btn">
            Выйти
          </button>
        </div>
      </header>

      <div className="admin-main-container">
        {/* Sidebar */}
        <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
          <nav className="admin-nav">
            {menuItems.map((item) => {
              const IconComponent = item.icon
              const showBadge = item.path === '/admin/messages' && unreadCount > 0
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  title={collapsed ? item.label : ''}
                >
                  {React.createElement(IconComponent as any, { size: 20, className: 'nav-icon' })}
                  {!collapsed && <span className="nav-label">{item.label}</span>}
                  {showBadge && (
                    <span className="nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                  )}
                </Link>
              )
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`admin-content ${collapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="admin-breadcrumb">
            <Link to="/admin">Главная</Link>
            <span>/</span>
            {(location.pathname.startsWith('/admin/events/') && location.pathname !== '/admin/events') ? (
              <>
                <Link to="/admin/events">События</Link>
                <span>/</span>
                <span>{getBreadcrumb()}</span>
              </>
            ) : (
              <span>{getBreadcrumb()}</span>
            )}
          </div>

          <Routes>
            <Route path="/" element={<Navigate to="/admin/donations" replace />} />
            <Route path="/donations" element={<DonationsAdmin />} />
            <Route path="/events/new" element={<EventForm />} />
            <Route path="/events/:id/edit" element={<EventForm />} />
            <Route path="/events" element={<EventsAdmin />} />
            <Route path="/companies" element={<CompaniesAdmin />} />
            <Route path="/letters-certificates" element={<LettersCertificatesAdmin />} />
            <Route path="/media/new" element={<MediaUploadForm />} />
            <Route path="/media" element={<MediaAdmin />} />
            <Route path="/plantings/new" element={<PlantingForm />} />
            <Route path="/plantings/:id/edit" element={<PlantingForm />} />
            <Route path="/plantings" element={<PlantingsAdmin />} />
            <Route path="/blog/new" element={<BlogPostForm />} />
            <Route path="/blog/:id/edit" element={<BlogPostForm />} />
            <Route path="/blog" element={<BlogAdmin />} />
            <Route path="/regions" element={<RegionsAdmin />} />
            <Route path="/messages" element={<MessagesAdmin />} />
            {user.role === 'admin' && (
              <>
                <Route path="/users" element={<UsersAdmin />} />
                <Route path="/settings" element={<SettingsAdmin />} />
              </>
            )}
          </Routes>
        </main>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="modal-overlay" onClick={() => setShowLogoutModal(false)}>
          <div className="modal-content max-w-[400px]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Подтверждение выхода</h2>
              <button className="modal-close" onClick={() => setShowLogoutModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="m-0 text-sm text-black/65">
                Вы уверены, что хотите выйти из системы?
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="btn-secondary"
              >
                Отмена
              </button>
              <button
                onClick={confirmLogout}
                className="btn-danger"
              >
                Выйти
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
